import { useMemo, useState } from 'react'
import { router, useForm } from '@inertiajs/react'
import { FileClock } from 'lucide-react'
import CreatorShell from '@/components/explore/CreatorShell'
import PriceSummary, {
  calculateSummary,
  formatBRL,
  type CouponState,
  type PlanOption,
  type Pricing,
  type SelectedService,
} from '@/components/explore/PriceSummary'
import WizardFooter, {
  WIZARD_FOOTER_SPACING,
} from '@/components/explore/WizardFooter'
import { PUBLIC_CONTAINER } from '@/components/public/publicShell'
import { cn } from '@/lib/utils'
import type { CityOption } from '@/types/publicSchool'
import StepAudience from './wizard/StepAudience'
import StepBusiness from './wizard/StepBusiness'
import StepCatalog from './wizard/StepCatalog'
import StepContact from './wizard/StepContact'
import FotosEVideos from '@/components/explore/FotosEVideos'
import StepHours from './wizard/StepHours'
import StepReview from './wizard/StepReview'
import StepServices from './wizard/StepServices'
import {
  EMPTY_HOURS,
  type Audience,
  type Category,
  type DraftPage,
  type DraftRow,
  type WizardForm,
} from './wizard/types'

interface Props {
  cities: CityOption[]
  categories: Category[]
  audiences: Audience[]
  pricing: Pricing
  total_steps: number
  step: number
  /** O rascunho sendo retomado. `null` no passo 1, antes de existir registro. */
  page: DraftPage | null
  /** Rascunhos pela metade, para quem entra do zero. */
  drafts: DraftRow[]
}

/**
 * Um título e uma pergunta por passo. A pergunta é o que a tela pede; o título
 * é o que a barra de progresso está contando.
 */
const STEPS = [
  { title: 'Serviços', question: 'O que o seu negócio faz?' },
  { title: 'Negócio', question: 'Como se chama e onde fica?' },
  { title: 'Contato', question: 'Como falam com você?' },
  { title: 'Catálogo', question: 'O que você vende e oferece?' },
  { title: 'Fotos e vídeos', question: 'Mostre o seu negócio' },
  { title: 'Horários', question: 'Quando você atende?' },
  { title: 'Sobre', question: 'Para quem você trabalha?' },
  { title: 'Revisão', question: 'Está tudo certo?' },
] as const

/**
 * Wizard de criação de página do BeWave Explore.
 *
 * Sete passos curtos, um assunto por tela, e RASCUNHO SALVO NO SERVIDOR a cada
 * avanço — dá para fechar a aba e retomar por Minhas Páginas. Antes eram
 * quatro telas longas com tudo em memória no browser: fechar a aba perdia o
 * preenchimento inteiro, e não havia onde pendurar as fotos do catálogo.
 *
 * O passo mostrado vem do SERVIDOR (`?passo=`), não de um `useState`: assim o
 * botão voltar do navegador funciona, o endereço é compartilhável e recarregar
 * a página não joga ninguém para o começo. O estado do formulário é que fica
 * no cliente, e sobrevive às trocas de passo porque as visitas usam
 * `preserveState`.
 *
 * O preço mostrado aqui é só espelho: o servidor recalcula tudo na publicação
 * e nunca confia no total do cliente (`Explore::PagesController#publish`).
 */
export default function NovaPagina({
  cities,
  categories,
  audiences,
  pricing,
  total_steps,
  step,
  page,
  drafts,
}: Props) {
  const form = useForm<WizardForm>({
    subcategory_ids: page?.subcategory_ids ?? [],
    name: page?.name ?? '',
    address: page?.address ?? '',
    city: page?.city ?? '',
    state: page?.state ?? '',
    postal_code: page?.postal_code ?? '',
    latitude: page?.latitude ?? null,
    longitude: page?.longitude ?? null,
    whatsapp: page?.whatsapp ?? '',
    website: page?.website ?? '',
    instagram: page?.instagram ?? '',
    tiktok: page?.tiktok ?? '',
    youtube: page?.youtube ?? '',
    operating_hours: page?.operating_hours ?? EMPTY_HOURS,
    audiences: page?.audiences ?? [],
    years_experience: page?.years_experience ?? '',
    extra_links:
      page?.extra_links && page.extra_links.length === 3
        ? page.extra_links
        : ['', '', ''],
    brief_description: page?.brief_description ?? '',
    // Sempre 3 posições, completando com vazio — são 3 caixas fixas na tela.
    // Padding em vez do `length === 3` usado nos extra_links: aquele descarta o
    // que foi salvo sempre que o servidor devolve um tamanho diferente.
    videos: [...(page?.videos ?? []), '', '', ''].slice(0, 3),
    plan: 'mensal',
    coupon_code: '',
  })
  const { data, setData } = form

  const [couponState, setCouponState] = useState<CouponState>({
    checking: false,
    valid: null,
    discountPct: null,
    freeMonths: null,
  })
  const [cartOpen, setCartOpen] = useState(false)

  /**
   * Os serviços marcados, na ORDEM em que foram marcados e já com o preço de
   * cada um — o primeiro paga cheio, os demais o adicional. É a mesma regra do
   * Explore::PricingTable, e é a ordem que o servidor cobra.
   */
  const selectedServices = useMemo<SelectedService[]>(
    () =>
      data.subcategory_ids.flatMap((id, index) => {
        const category = categories.find((c) =>
          c.subcategories.some((s) => s.id === id),
        )
        const subcategory = category?.subcategories.find((s) => s.id === id)
        if (!category || !subcategory) return []

        return [
          {
            id: subcategory.id,
            label: subcategory.label,
            categoryLabel: category.label,
            price: index === 0 ? pricing.first_service : pricing.extra_service,
          },
        ]
      }),
    [data.subcategory_ids, categories, pricing],
  )

  const pricingSummary = useMemo(
    () =>
      calculateSummary(
        pricing,
        data.subcategory_ids.length,
        data.plan,
        couponState,
      ),
    [pricing, data.subcategory_ids.length, data.plan, couponState],
  )

  const toggleSubcategory = (id: number) => {
    setData(
      'subcategory_ids',
      data.subcategory_ids.includes(id)
        ? data.subcategory_ids.filter((v) => v !== id)
        : // `push` no fim e não ordenação: quem foi marcado primeiro paga o
          // preço cheio, e essa ordem é a que o servidor cobra.
          [...data.subcategory_ids, id],
    )
  }

  const checkCoupon = async () => {
    if (!data.coupon_code.trim()) {
      setCouponState({
        checking: false,
        valid: null,
        discountPct: null,
        freeMonths: null,
      })
      return
    }
    setCouponState((s) => ({ ...s, checking: true }))
    try {
      const response = await fetch(
        `/cupons/${encodeURIComponent(data.coupon_code.trim())}`,
        { headers: { Accept: 'application/json' } },
      )
      if (!response.ok) {
        setCouponState({
          checking: false,
          valid: false,
          discountPct: null,
          freeMonths: null,
        })
        return
      }
      const body = await response.json()
      setCouponState({
        checking: false,
        valid: Boolean(body.valid),
        discountPct: body.discount_pct ?? null,
        freeMonths: body.free_months ?? null,
      })
      // Cortesia manda no plano — o servidor força o mesmo na publicação
      // (ExploreCoupon#forced_plan). Deixar "Anual" marcado aqui mostraria
      // "12x R$ 0,00" para um brinde de 6 meses.
      if (body.valid && body.forced_plan) {
        setData('plan', body.forced_plan as PlanOption['key'])
      }
    } catch {
      setCouponState({
        checking: false,
        valid: false,
        discountPct: null,
        freeMonths: null,
      })
    }
  }

  /** O que cada passo grava. O que não está aqui não é mexido pelo passo. */
  const payloadForStep = (current: number) => {
    switch (current) {
      case 1:
        return { subcategory_ids: data.subcategory_ids }
      case 2:
        return {
          name: data.name,
          address: data.address,
          city: data.city,
          state: data.state,
          postal_code: data.postal_code,
          latitude: data.latitude,
          longitude: data.longitude,
        }
      case 3:
        return {
          whatsapp: data.whatsapp,
          website: data.website,
          instagram: data.instagram,
          tiktok: data.tiktok,
          youtube: data.youtube,
        }
      // O catálogo não passa por aqui: cada item tem endpoint próprio, porque
      // tem arquivo junto (ver StepCatalog).
      case 4:
        return {}
      // As FOTOS do passo 5 também têm endpoint próprio e já subiram; os links
      // de vídeo são campo de formulário e é aqui que eles gravam.
      case 5:
        return { videos: data.videos }
      case 6:
        return { operating_hours: data.operating_hours }
      case 7:
        return {
          audiences: data.audiences,
          years_experience: data.years_experience,
          brief_description: data.brief_description,
          extra_links: data.extra_links,
        }
      default:
        return {}
    }
  }

  const goToStep = (target: number) => {
    // Passo 1 sem rascunho: é o POST que faz a página nascer.
    if (!page) {
      form.transform(() => ({ subcategory_ids: data.subcategory_ids }))
      form.post('/nova-pagina', { preserveScroll: true })
      return
    }

    // `transform` decide o que sai no lugar do formulário inteiro: cada passo
    // manda só os campos dele, e o servidor grava só o que veio.
    form.transform(() => ({
      ...payloadForStep(step),
      passo: target,
      passo_atual: step,
    }))
    form.patch(`/nova-pagina/${page.id}`, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const publish = () => {
    if (!page) return

    form.transform(() => ({ plan: data.plan, coupon_code: data.coupon_code }))
    form.post(`/nova-pagina/${page.id}/publicar`)
  }

  const audienceLabels = audiences
    .filter((a) => data.audiences.includes(a.key))
    .map((a) => a.label)

  // O que trava o "Continuar" em cada passo, e a frase que explica a trava.
  // Passos 4, 5 e 6 são opcionais de propósito: catálogo, fotos e horário
  // melhoram muito a página, mas exigi-los seria pedágio.
  const blockers: Record<number, string | null> = {
    1:
      data.subcategory_ids.length === 0 ? 'Marque pelo menos um serviço' : null,
    2: data.name.trim().length < 2 ? 'Digite o nome do seu negócio' : null,
    3:
      data.whatsapp.replace(/\D/g, '').length < 10
        ? 'Digite seu WhatsApp com DDD'
        : null,
    7:
      data.audiences.length === 0
        ? 'Escolha pelo menos um público que você atende'
        : null,
  }
  const blocker = blockers[step] ?? null

  const last = step === total_steps
  const meta = STEPS[step - 1] ?? STEPS[0]

  return (
    <CreatorShell title="Criar página — BeWave Explore" cities={cities}>
      <div
        className={cn(PUBLIC_CONTAINER, 'flex-1 pt-8', WIZARD_FOOTER_SPACING)}
      >
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          Passo {step} de {total_steps} · {meta.title}
        </p>
        <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">
          {meta.question}
        </h1>

        {!page && drafts.length > 0 && <DraftsBanner drafts={drafts} />}

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_24rem]">
          <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
            {step === 1 && (
              <StepServices
                categories={categories}
                subcategoryIds={data.subcategory_ids}
                pricing={pricing}
                onToggle={toggleSubcategory}
              />
            )}
            {step === 2 && (
              <StepBusiness
                data={data}
                setData={setData}
                errors={form.errors}
              />
            )}
            {step === 3 && (
              <StepContact data={data} setData={setData} errors={form.errors} />
            )}
            {step === 4 && page && (
              <StepCatalog pageId={page.id} items={page.items} />
            )}
            {step === 5 && page && (
              <FotosEVideos
                pageId={page.id}
                cover={page.cover}
                photos={page.photos}
                videos={data.videos}
                onVideosChange={(next) => setData('videos', next)}
                videosError={form.errors.videos}
                intro="Uma foto boa é o que faz alguém parar no seu card. A capa aparece na busca do Explore; as outras montam a galeria da sua página. Pode pular e voltar depois."
              />
            )}
            {step === 6 && (
              <StepHours
                hours={data.operating_hours}
                onChange={(next) => setData('operating_hours', next)}
              />
            )}
            {step === 7 && (
              <StepAudience
                data={data}
                setData={setData}
                audiences={audiences}
                errors={form.errors}
              />
            )}
            {step === 8 && (
              <StepReview
                data={data}
                categories={categories}
                items={page?.items ?? []}
                audienceLabels={audienceLabels}
                hasCover={Boolean(page?.cover)}
                photoCount={(page?.cover ? 1 : 0) + (page?.photos.length ?? 0)}
                videoCount={data.videos.filter((link) => link.trim()).length}
                onEditStep={(target) =>
                  page &&
                  router.get(
                    `/nova-pagina/${page.id}`,
                    { passo: target },
                    { preserveState: true, preserveScroll: true },
                  )
                }
              />
            )}
          </div>

          {/* Coluna direita no desktop; gaveta no celular, aberta pelo total
              que fica na barra de ação. */}
          <PriceSummary
            pricing={pricing}
            services={selectedServices}
            plan={data.plan}
            onPlanChange={(plan) => setData('plan', plan)}
            couponCode={data.coupon_code}
            onCouponCodeChange={(value) => setData('coupon_code', value)}
            couponState={couponState}
            onCheckCoupon={checkCoupon}
            summary={pricingSummary}
            open={cartOpen}
            onOpenChange={setCartOpen}
          />
        </div>
      </div>

      <WizardFooter
        step={step}
        totalSteps={total_steps}
        busy={form.processing}
        nextDisabled={Boolean(blocker)}
        nextLabel={last ? 'Gerar minha página' : 'Continuar'}
        hint={blocker}
        onBack={() =>
          page &&
          router.get(
            `/nova-pagina/${page.id}`,
            { passo: Math.max(1, step - 1) },
            { preserveState: true, preserveScroll: true },
          )
        }
        onNext={() => (last ? publish() : goToStep(step + 1))}
        cart={{
          total: formatBRL(pricingSummary.periodTotal),
          caption:
            pricingSummary.servicesCount === 0
              ? 'Escolha um serviço'
              : `${pricingSummary.servicesCount} × ${pricingSummary.plan.label}`,
          onOpen: () => setCartOpen(true),
        }}
      />
    </CreatorShell>
  )
}

/**
 * "Você tem um rascunho em andamento."
 *
 * Aparece só para quem abre /nova-pagina do zero. Sem isto, quem parasse no
 * meio e voltasse pelo mesmo link começaria uma segunda página sem perceber —
 * e a primeira ficaria pendurada na conta.
 */
function DraftsBanner({ drafts }: { drafts: DraftRow[] }) {
  return (
    <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
      <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
        <FileClock className="size-4" aria-hidden="true" />
        {drafts.length === 1
          ? 'Você tem uma página pela metade'
          : `Você tem ${drafts.length} páginas pela metade`}
      </p>
      <ul className="mt-2 space-y-1.5">
        {drafts.map((draft) => (
          <li
            key={draft.id}
            className="flex items-center justify-between gap-3"
          >
            <span className="min-w-0 truncate text-sm text-amber-900">
              {draft.name || 'Sem nome ainda'}
            </span>
            <a
              href={draft.href}
              className="shrink-0 text-sm font-semibold text-amber-900 underline underline-offset-2"
            >
              Retomar
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}
